export type RestaurantLocaleConfig = {
  currency_code: string;
  currency_symbol: string;
  country_iso2: string;
};

const DEFAULT_LOCALE_CONFIG: RestaurantLocaleConfig = {
  currency_code: "GBP",
  currency_symbol: "£",
  country_iso2: "GB",
};

let activeLocaleConfig: RestaurantLocaleConfig = { ...DEFAULT_LOCALE_CONFIG };
let lastAppliedConfigKey = "";
let cachedCurrencyFormatter: Intl.NumberFormat | null = null;
let cachedNumberFormatter: Intl.NumberFormat | null = null;

function invalidateFormatterCache(): void {
  cachedCurrencyFormatter = null;
  cachedNumberFormatter = null;
}

export function resolveLocaleTag(countryIso2: string): string {
  const iso2 = countryIso2.trim().toUpperCase();
  if (!iso2) return "en-GB";
  return `en-${iso2}`;
}

export function setRestaurantLocaleConfig(config: RestaurantLocaleConfig): void {
  activeLocaleConfig = {
    currency_code: config.currency_code.trim() || DEFAULT_LOCALE_CONFIG.currency_code,
    currency_symbol: config.currency_symbol.trim() || DEFAULT_LOCALE_CONFIG.currency_symbol,
    country_iso2: config.country_iso2.trim().toUpperCase() || DEFAULT_LOCALE_CONFIG.country_iso2,
  };
  invalidateFormatterCache();
}

/** Idempotent sync from restaurant configuration — safe during render. */
export function applyRestaurantLocaleConfig(config: RestaurantLocaleConfig): void {
  const key = `${config.currency_code}|${config.currency_symbol}|${config.country_iso2}`;
  if (key === lastAppliedConfigKey) return;
  lastAppliedConfigKey = key;
  setRestaurantLocaleConfig(config);
}

export function getRestaurantLocaleConfig(): Readonly<RestaurantLocaleConfig> {
  return activeLocaleConfig;
}

export function getCurrencyCode(): string {
  return activeLocaleConfig.currency_code;
}

export function getCurrencySymbol(): string {
  return activeLocaleConfig.currency_symbol;
}

export function getCountryIso2(): string {
  return activeLocaleConfig.country_iso2;
}

function getCurrencyFormatter(): Intl.NumberFormat {
  if (!cachedCurrencyFormatter) {
    const { currency_code, country_iso2 } = activeLocaleConfig;
    cachedCurrencyFormatter = new Intl.NumberFormat(resolveLocaleTag(country_iso2), {
      style: "currency",
      currency: currency_code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return cachedCurrencyFormatter;
}

function getNumberFormatter(): Intl.NumberFormat {
  if (!cachedNumberFormatter) {
    const { country_iso2 } = activeLocaleConfig;
    cachedNumberFormatter = new Intl.NumberFormat(resolveLocaleTag(country_iso2), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  return cachedNumberFormatter;
}

function toSafeNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Locale-aware number without currency symbol. */
export function formatNumber(value: unknown): string {
  const safe = toSafeNumber(value);
  try {
    return getNumberFormatter().format(safe);
  } catch {
    return String(safe);
  }
}

/** Locale-aware currency using restaurant `currency_code` and `country_iso2`. */
export function formatCurrency(value: unknown): string {
  const safe = toSafeNumber(value);
  try {
    return getCurrencyFormatter().format(safe);
  } catch {
    return `${getCurrencySymbol()}${safe.toFixed(2)}`;
  }
}

/** Primary UI money formatter — NaN/invalid → zero in restaurant currency. */
export function formatMoney(value: unknown): string {
  return formatCurrency(value);
}

export function createCurrencyFormatter(
  currencyCode: string,
  countryIso2?: string
): (value: unknown) => string {
  const code = currencyCode.trim();
  const locale = resolveLocaleTag(countryIso2 ?? activeLocaleConfig.country_iso2);
  let formatter: Intl.NumberFormat | null = null;

  const getFormatter = (): Intl.NumberFormat => {
    if (!formatter) {
      formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return formatter;
  };

  return (value: unknown) => {
    const safe = toSafeNumber(value);
    try {
      return getFormatter().format(safe);
    } catch {
      return `${getCurrencySymbol()}${safe.toFixed(2)}`;
    }
  };
}

/** Test-only reset. */
export function resetRestaurantLocaleConfigForTests(): void {
  activeLocaleConfig = { ...DEFAULT_LOCALE_CONFIG };
  lastAppliedConfigKey = "";
  invalidateFormatterCache();
}
